import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

export interface ConfirmDialogSecondaryAction {
  text: string;
  onClick: () => void | Promise<void>;
  type?: 'danger' | 'warning' | 'ghost';
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning';
  secondaryAction?: ConfirmDialogSecondaryAction;
  isConfirming?: boolean;
  confirmingText?: string;
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  type = 'danger',
  secondaryAction,
  isConfirming = false,
  confirmingText = 'Processando...',
}: ConfirmDialogProps) {
  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch {
      // Caller is responsible for surfacing the error; modal stays open
    }
  };

  const secondaryClass = secondaryAction?.type === 'danger'
    ? 'ac-btn--danger'
    : secondaryAction?.type === 'warning'
      ? 'ac-btn--warning'
      : 'ac-btn--ghost';

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002,
        padding: '20px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid var(--panel-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: type === 'danger' ? '#fef2f2' : '#fffbeb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: type === 'danger' ? '#fee2e2' : '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AlertTriangle
                size={20}
                color={type === 'danger' ? '#dc2626' : '#f59e0b'}
              />
            </div>
            <h3 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text)'
            }}>
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--muted)',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '24px',
          fontSize: '14px',
          lineHeight: '1.6',
          color: 'var(--text)'
        }}>
          {message}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid var(--panel-border)',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={onClose}
            disabled={isConfirming}
            className="ac-btn ac-btn--ghost"
          >
            {cancelText}
          </button>
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              disabled={isConfirming}
              className={`ac-btn ${secondaryClass}`}
            >
              {secondaryAction.text}
            </button>
          )}
          <button
            onClick={handleConfirm}
            disabled={isConfirming}
            className={`ac-btn ${type === 'danger' ? 'ac-btn--danger' : 'ac-btn--warning'}`}
          >
            {isConfirming ? confirmingText : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
